import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import { DataTable } from "../../components/DataTable.jsx";
import { useAuth } from "../login/AuthContext";
import { useNavigate } from "react-router-dom";
import { AddCircleOutline } from "@mui/icons-material";
import axios from "axios";
import config from "../../config/config.js";
import IconActionsRenderer from "../../components/IconActionsRenderer.jsx";
import { dateFormatter } from "../../components/DataTable/dateFormatter.js";
import { StatusCellRenderer } from "../../components/DataTable/StatusCellRenderer.jsx";

const OrderPage = () => {
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const searchTimeoutRef = useRef(null);
  const dataTableRef = useRef(null);
  const [aktifPasif, setAktifPasif] = useState("aktif");
  const isBayiFullAccess = Number(userData?.BayiId ?? userData?.BayiKodu ?? 0) === 1;
  const customQueryParams = useMemo(() => ({ aktifPasif }), [aktifPasif]);

  useEffect(() => {
    if (!dataTableRef.current?.refreshData) return;
    const t = setTimeout(() => dataTableRef.current.refreshData(), 0);
    return () => clearTimeout(t);
  }, [aktifPasif]);

  const toggleDrawer = () => navigate("/siparis");

  // Sipariş silme (aktif listeden silince silinen tabloya kopyala; pasif listeden Bayi 1 kalıcı siler)
  const handleDeleteOrder = useCallback(
    async (id) => {
      const api = axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` },
      });
      return api.put(`/siparis/delete/${id}`, { params: { aktifPasif } });
    },
    [token, aktifPasif]
  );

  // Sipariş yönlendirme işlevi
  const handleForwardOrder = async (orderId) => {
    try {
      const api = axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` }
      });
      return await api.put(`/siparis/${orderId}/yonlendir`);
    } catch (error) {
      console.error('Sipariş yönlendirme hatası:', error.response?.data);
      throw error;
    }
  };

  const defaultOrderColumnDefs = useMemo(() => [
    {
      headerName: 'İşlemler',
      field: 'actions',
      width: 130,
      minWidth: 130,
      maxWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: IconActionsRenderer,
      pinned: 'left',
      cellClass: 'action-cell-center'
    },
    {
      headerName: 'Sipariş Tarihi',
      field: 'Tarih',
      valueFormatter: dateFormatter,
      sortable: true,
      filter: 'agDateColumnFilter',
      width: 120,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true,
        comparator: function(filterLocalDateAtMidnight, cellValue) {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0;
        },
        browserDatePicker: true,
        minValidYear: 2020,
        maxValidYear: 2050,
        inRangeFloatingFilterDateFormat: 'DD.MM.YYYY'
      }
    },
    {
      headerName: 'Sipariş No',
      field: 'Id',
      sortable: true,
      filter: 'agNumberColumnFilter',
      width: 110,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Bayi',
      field: 'BayiAdi',
      sortable: true,
      filter: true,
      width: 100,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Yönlendirilen Bayi',
      field: 'YonlendirilenBayiAdi',
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.data ? params.value || 'Yönlendirilmedi' : '',
      width: 150,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Lisans No',
      field: 'MusteriNo',
      sortable: true,
      filter: true,
      width: 120,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Lisans Tipi',
      field: 'LisansTipi',
      sortable: true,
      filter: true,
      width: 120,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Paket',
      field: 'Hareketler',
      sortable: true,
      filter: true,
      width: 120,
      valueGetter: (p) =>
        p.data?.Hareketler?.[0]?.PaketAdi ||
        p.data?.Hareketler?.[0]?.Paket?.Adi ||
        p.data?.Hareketler?.[0]?.name ||
        "",
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Firma Adı',
      field: 'Unvan',
      sortable: true,
      filter: true,
      width: 300,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Başlangıç Tarihi',
      field: 'LisansBaslangicTarihi',
      valueFormatter: dateFormatter,
      sortable: true,
      filter: 'agDateColumnFilter',
      width: 140,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true,
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0;
        },
        browserDatePicker: true,
        minValidYear: 2020,
        maxValidYear: 2050,
        inRangeFloatingFilterDateFormat: 'DD.MM.YYYY'
      }
    },
    {
      headerName: 'Bitiş Tarihi',
      field: 'LisansBitisTarihi',
      valueFormatter: dateFormatter,
      sortable: true,
      filter: 'agDateColumnFilter',
      width: 140,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true,
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0;
        },
        browserDatePicker: true,
        minValidYear: 2020,
        maxValidYear: 2050,
        inRangeFloatingFilterDateFormat: 'DD.MM.YYYY'
      }
    },
    {
      headerName: 'Durum',
      field: 'DurumAd',
      sortable: true,
      filter: true,
      width: 140,
      cellRenderer: StatusCellRenderer,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    }
  ], []);

  // Search input değiştiğinde debounce ile refreshData tetikle
  const handleSearch = (value) => {
    setSearchText(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }
    }, 300);
  };

  return (
    <div className="cari-listesi-container">
      <div className="header">
        <h1>Siparişler</h1>
      </div>

      {isBayiFullAccess && (
        <FormControl component="fieldset" sx={{ mb: 1.5, display: "block" }}>
          <FormLabel component="legend" sx={{ fontSize: "0.875rem", mb: 0.5 }}>
            Liste
          </FormLabel>
          <RadioGroup
            row
            value={aktifPasif}
            onChange={(e) => setAktifPasif(e.target.value)}
            name="aktif-pasif-siparis"
          >
            <FormControlLabel value="aktif" control={<Radio size="small" />} label="Aktif" />
            <FormControlLabel value="pasif" control={<Radio size="small" />} label="Pasifler" />
          </RadioGroup>
        </FormControl>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Gelişmiş Arama"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutline />}
          onClick={toggleDrawer}
          sx={{ ml: 2 }}
          size="medium"
        >
          Ekle
        </Button>
      </div>

      <DataTable
        ref={dataTableRef}
        searchText={searchText}
        tableType="orders"
        columnDefs={defaultOrderColumnDefs}
        apiEndpoint="/siparis"
        searchEndpoint="/siparis/search"
        editPath="/siparis"
        onRowDoubleClicked={(e) => { const id = e.data?.Id; if (id != null) navigate(`/siparis/${id}`); }}
        customQueryParams={customQueryParams}
        onForwardAction={handleForwardOrder}
        onDeleteAction={handleDeleteOrder}
        forwardConfirmMessage="Bu siparişi üst bayiye yönlendirmek istediğinizden emin misiniz?"
        deleteConfirmMessage={aktifPasif === "pasif" ? "Bu siparişi kalıcı olarak silmek istediğinizden emin misiniz?" : "Bu siparişi silmek istediğinizden emin misiniz?"}
      />
    </div>
  );
};

export default OrderPage;
